// Import main SCSS file
import "../scss/main.scss";
// Main JS entry point
// Instead of pulling in the full bootstrap bundle (all plugins + Popper),
// only import the plugins we actually use.
import "bootstrap/js/dist/collapse";
import "bootstrap/js/dist/dropdown";
// Import search functionality
import "./search";
// Import Socials Share functionality
import "./share-recipe";
// Import Gravity Forms tooltip behavior
import "./tool-tip";
// Import Sirv Thumbnail Slider
import "./product-thumbnails-slider";

// CTA tiles: equalize content heights
import { initCtaTiles } from "./cta-tiles";

console.log("Topco theme JS loaded");
const isTestEnv =
  typeof process !== "undefined" && process.env.NODE_ENV === "test";

/**
 * Detect if hero slide titles are multi-line and adjust positioning
 */
const adjustTitlePositioning = () => {
  const titles = document.querySelectorAll(".hero-slide__title");

  titles.forEach((title) => {
    // Remove classes first to get accurate measurements and avoid stale state
    title.classList.remove(
      "hero-slide__title--multiline",
      "hero-slide__title--singleline",
    );

    // Force reflow to get accurate measurements
    void title.offsetHeight;

    // Get the computed line height and total height
    const computedStyle = window.getComputedStyle(title);
    let lineHeight = parseFloat(computedStyle.lineHeight);
    const totalHeight = title.offsetHeight;

    // If lineHeight is "normal", calculate it from font-size
    if (isNaN(lineHeight) || computedStyle.lineHeight === "normal") {
      const fontSize = parseFloat(computedStyle.fontSize);
      lineHeight = fontSize * 1.2; // typical "normal" multiplier
    }

    // Calculate number of lines using a stricter ratio
    const ratio = totalHeight / lineHeight;
    const lineCount = Math.round(ratio);

    // Debug: log values to help troubleshoot
    // console.log('Title:', title.textContent);
    // console.log('Line height:', lineHeight);
    // console.log('Total height:', totalHeight);
    // console.log('Line count:', lineCount);

    // if (lineCount === 1) {
    //   title.classList.add("hero-slide__title--singleline");
    // } else
    if (lineCount === 2) {
      title.classList.add("hero-slide__title--multiline");
    }
  });
};

// Re-check on window resize
let resizeTimer;
window.addEventListener("resize", () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    adjustTitlePositioning();
  }, 250);
});

/**
 * Debug helper: show viewport rect width/height in top-right corner
 */
const initViewportRectDebug = () => {
  const existing = document.querySelector("#viewport-rect-debug");
  const el = existing || document.createElement("div");

  if (!existing) {
    el.id = "viewport-rect-debug";
    el.style.position = "fixed";
    el.style.bottom = "0.5rem";
    el.style.right = "0.5rem";
    el.style.zIndex = "9999";
    el.style.padding = "0.25rem 0.5rem";
    el.style.background = "rgba(0, 0, 0, 0.7)";
    el.style.color = "#fff";
    el.style.fontSize = "12px";
    el.style.fontFamily =
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    el.style.borderRadius = "3px";
    el.style.pointerEvents = "none";
    document.body.appendChild(el);
  }

  const update = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    el.textContent = `size: ${width} × ${height}`;
  };

  // Initial render
  update();

  // Update on resize
  window.addEventListener("resize", update);
};

// Custom JavaScript for the theme
document.addEventListener("DOMContentLoaded", () => {
  // Lazy-load heavier modules only on pages that need them.
  // This keeps the main entrypoint small and speeds up initial page load.
  if (!isTestEnv) {
    if (document.querySelector(".hero-slider__wrapper")) {
      void import("./hero-slider");
    }

    if (document.querySelector("[data-featured-products-slider]")) {
      void import("./featured_products_slider");
    }

    if (document.querySelector(".retailers-page")) {
      void import("./retailers").then(({ initRetailersFilters }) => {
        initRetailersFilters();
      });
    }
  }

  // Initialize any custom functionality here

  // Debug: show viewport rect width/height in top-right corner (non-production only)
  const host = window.location.hostname;
  if (
    host.includes("localhost") ||
    host.includes("127.0.0.1") ||
    host.includes("fullcirclemdev.wpenginepowered.com")
  ) {
    initViewportRectDebug();
  }

  // Adjust hero slide title positioning
  adjustTitlePositioning();

  // Equalize CTA tile content heights
  // initCtaTiles();

  // Navbar elements
  const navbarToggler = document.querySelector(".navbar-toggler");
  const navbar = document.querySelector(".navbar");
  const mobileMenuDrawer = document.querySelector("#mobileNavbar");
  const navLogo = document.querySelector(".navbar-brand");

  // Toggle navbar and hamburger animation on mobile menu open/close
  if (navbarToggler && navbar && mobileMenuDrawer && navLogo) {
    mobileMenuDrawer.addEventListener("show.bs.collapse", () => {
      navbar.classList.add("menu-open");
      navbarToggler.classList.add("open");
      navLogo.classList.add("menu-open");
    });

    mobileMenuDrawer.addEventListener("hide.bs.collapse", () => {
      navbar.classList.remove("menu-open");
      navbarToggler.classList.remove("open");
      navLogo.classList.remove("menu-open");
    });
  }

  // Toggle navbar background on scroll
  if (navbar && navLogo) {
    let isShrunk = false;
    let isAtTop = true;
    let ticking = false;

    window.addEventListener("scroll", () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrolled = window.scrollY > 100;
          const atTop = window.scrollY === 0;

          if (scrolled !== isShrunk) {
            navbar.classList.toggle("scrolled", scrolled);
            navLogo.classList.toggle("shrink", scrolled);
            isShrunk = scrolled;
          }

          if (atTop !== isAtTop) {
            navLogo.classList.toggle("at-top", atTop);
            isAtTop = atTop;
          }

          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // Example: Smooth scrolling for anchor links
  document.querySelectorAll("a[href^='#']").forEach((anchor) => {
    anchor.addEventListener("click", function (e) {
      const href = this.getAttribute("href");

      // Skip if it's just "#" or not a valid ID selector
      if (!href || href === "#" || href.length <= 1) {
        return;
      }
      // Skip share buttons
      if (
        this.closest(".recipe-share") ||
        this.classList.contains("social-btn")
      ) {
        return;
      }
      e.preventDefault();
      try {
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      } catch (error) {
        // Invalid selector, ignore
        console.warn("Invalid anchor selector:", href);
      }
    });
  });

  // Filter buttons
  const filterButtons = document.querySelectorAll(".recipe-filter-btn");
  const recipeCards = document.querySelectorAll(".recipe-card");

  if (filterButtons.length && recipeCards.length) {
    filterButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const selectedCategory = this.getAttribute("data-category") || "all";

        filterButtons.forEach((btn) => {
          btn.classList.toggle("active", btn === button);
        });

        recipeCards.forEach((card) => {
          const catsString = card.getAttribute("data-categories") || "";
          const categories = catsString.split(" ").filter(Boolean);

          if (
            selectedCategory === "all" ||
            categories.indexOf(selectedCategory) !== -1
          ) {
            card.classList.remove("d-none");
          } else {
            card.classList.add("d-none");
          }
        });
      });
    });
  }
});
